import { useEffect, useState } from 'react';
import { Bell, BellOff, Smartphone } from 'lucide-react';
import { api } from '@/lib/api';
import { t } from '@/lib/i18n';
import { Button, Card, ErrorBanner, Notice } from './ui';

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const standalone = () => window.matchMedia?.('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true;

/** Turn push notifications on/off for this device (phone or computer). */
export function PushCard() {
  const supported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  const [state, setState] = useState<'loading' | 'on' | 'off' | 'blocked' | 'unsupported'>(supported ? 'loading' : 'unsupported');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [tested, setTested] = useState(false);

  useEffect(() => {
    if (!supported) return;
    (async () => {
      if (Notification.permission === 'denied') return setState('blocked');
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      setState(sub ? 'on' : 'off');
    })().catch(() => setState('off'));
  }, [supported]);

  const enable = async () => {
    setBusy(true);
    setError(null);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        setState(perm === 'denied' ? 'blocked' : 'off');
        return;
      }
      const reg = (await navigator.serviceWorker.getRegistration()) ?? (await navigator.serviceWorker.register('/sw.js'));
      await navigator.serviceWorker.ready;
      const { publicKey } = await api.get<{ publicKey: string }>('/push/public-key');
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource });
      await api.post('/push/subscribe', sub.toJSON());
      setState('on');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const disable = async () => {
    setBusy(true);
    setError(null);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        await api.post('/push/unsubscribe', { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setState('off');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const [iosPre, iosPost] = t('On iPhone: open this site in Safari, tap Share → {action}, open the app from the home screen, then come back here (needs iOS 16.4 or later).').split('{action}');
  return (
    <Card title={<span className="flex items-center gap-2"><Smartphone className="h-4 w-4 text-brand-600" /> {t('Notifications on this device')}</span>} className="mb-6">
      <ErrorBanner error={error} />
      <p className="mb-3 text-sm text-slate-600">
        {t('Get leave approvals, missing clock-out reminders and reports as phone or computer notifications, even when the app is closed. Only a title and a short line are sent.')}
      </p>
      {state === 'unsupported' ? (
        isIos() && !standalone() ? (
          <Notice>
            {iosPre}
            <b>{t('Add to Home Screen')}</b>
            {iosPost}
          </Notice>
        ) : (
          <Notice>{t('This browser does not support notifications.')}</Notice>
        )
      ) : state === 'blocked' ? (
        <Notice tone="amber">{t('Notifications are blocked for this site. Allow them in the browser or phone settings, then reload this page.')}</Notice>
      ) : state === 'on' ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700"><Bell className="h-4 w-4" /> {t('On for this device')}</span>
          <Button
            variant="secondary"
            size="sm"
            onClick={async () => {
              await api.post('/push/test', {});
              setTested(true);
            }}
          >
            {t('Send a test')}
          </Button>
          <Button variant="ghost" size="sm" icon={<BellOff className="h-3.5 w-3.5" />} loading={busy} onClick={disable}>{t('Turn off')}</Button>
          {tested && <span className="text-xs text-slate-500">{t('Sent — it should arrive within a few seconds.')}</span>}
        </div>
      ) : (
        <Button icon={<Bell className="h-4 w-4" />} loading={busy || state === 'loading'} onClick={enable}>{t('Turn on notifications')}</Button>
      )}
    </Card>
  );
}
